/**
 * GTM Provider 模块，负责注入埋点能力。
 */

import type { ReactNode } from "react";
import { createContext, useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { initGTM, trackEvent, trackPageView } from "../utils/gtm";

type GtmContextValue = {
	trackEvent: typeof trackEvent;
	trackPageView: typeof trackPageView;
};

const GTMContext = createContext<GtmContextValue | null>(null);

type GTMProviderProps = {
	children: ReactNode;
};

/**
 * GTM Provider 组件。
 * @param {GTMProviderProps} props 组件参数
 */
export const GTMProvider = ({ children }: GTMProviderProps) => {
	const location = useLocation();

	useEffect(() => {
		// 初始化GTM
		initGTM();
	}, []);

	useEffect(() => {
		// 页面变化时跟踪
		if (location.pathname) {
			trackPageView(location.pathname, document.title);
		}
	}, [location.pathname]);

	const gtm: GtmContextValue = {
		trackEvent,
		trackPageView,
	};

	return <GTMContext.Provider value={gtm}>{children}</GTMContext.Provider>;
};

/**
 * 获取 GTM 上下文实例。
 */
export const useGTM = () => {
	const context = useContext(GTMContext);
	if (!context) {
		throw new Error("useGTM must be used within a GTMProvider");
	}
	return context;
};
